import { useState } from "react";
import { api } from "../lib/api";
import { Campo, Modal } from "./piezas";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

export interface SocioCobro {
  id: string;
  nombre: string;
  apellidos: string;
}

/** Cobro de cuota: importe, periodo y forma de pago. Lo usan la ficha, el mostrador y renovaciones. */
export default function DialogoCobro({
  socio,
  importePorDefecto,
  alCerrar,
  alHecho,
  alFallar,
}: {
  socio: SocioCobro;
  importePorDefecto: number;
  alCerrar: () => void;
  alHecho: (periodoFin: string) => void;
  alFallar: (mensaje: string) => void;
}) {
  const { t } = useIdioma();
  const [importe, setImporte] = useState(String(importePorDefecto));
  const [metodoPago, setMetodoPago] = useState("EFECTIVO");
  const [anios, setAnios] = useState(1);
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("dc.titulo", { socio: `${socio.nombre} ${socio.apellidos}` })}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            {t("g.cancelar")}
          </button>
          <button
            className="boton"
            disabled={enviando || !(Number(importe) >= 0)}
            onClick={async () => {
              setEnviando(true);
              try {
                const cuota = await api<{ periodoFin: string }>("/cuotas", {
                  metodo: "POST",
                  cuerpo: { socioId: socio.id, importeEuros: Number(importe), metodoPago, anios },
                });
                alHecho(cuota.periodoFin);
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("dc.fallo"));
              } finally {
                setEnviando(false);
              }
            }}
          >
            {enviando ? t("dc.cobrando") : t("dc.cobrar")}
          </button>
        </>
      }
    >
      <div className="linea-campos">
        <Campo etiqueta={t("dc.importe")}>
          <input type="number" min={0} step="0.01" value={importe} onChange={(e) => setImporte(e.target.value)} autoFocus />
        </Campo>
        <Campo etiqueta={t("dc.periodo")}>
          <select
            value={anios}
            onChange={(e) => {
              const n = Number(e.target.value);
              setAnios(n);
              setImporte((importePorDefecto * n).toFixed(2));
            }}
          >
            <option value={1}>{t("dc.unAnio")}</option>
            <option value={2}>{t("dc.dosAnios")}</option>
            <option value={3}>{t("dc.tresAnios")}</option>
          </select>
        </Campo>
      </div>
      <Campo etiqueta={t("dc.formaDePago")}>
        <select value={metodoPago} onChange={(e) => setMetodoPago(e.target.value)}>
          {["EFECTIVO", "TARJETA", "TRANSFERENCIA"].map((m) => (
            <option key={m} value={m}>
              {t(`pago.${m}` as Clave)}
            </option>
          ))}
          <option value="CARTERA">{t("dc.carteraDelSocio")}</option>
        </select>
      </Campo>
      <p className="suave pequeno" style={{ marginBottom: 0 }}>
        {t("dc.nota")}
      </p>
    </Modal>
  );
}
